import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AngularModule } from '../../../../modules/angular.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { NavigationService } from '../../../../services/navigation.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { SectionService } from '../../../../services/section.service';
import { ClientService } from '../../../../services/client.service';
import { Client, Section } from '@mira-avant/mtlibs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AuthService } from '../../../../services/auth.service';

export interface ListItem {
	uuid: string;
	[key: string]: any;
}

@Component({
	selector: 'app-list-item',
	standalone: true,
	imports: [AngularModule, ZorroModule],
	templateUrl: './list-item.component.html',
	styleUrl: './list-item.component.scss',
})
export class ListItemComponent {
	@Input('enableSelect') public enableSelect: boolean = false;
	@Input('enableUpDown') public enableUpDown: boolean = false;
	@Input('enableCopy') public enableCopy: boolean = false;
	@Input('enableEdit') public enableEdit: boolean = false;
	@Input('enableDelete') public enableDelete: boolean = false;
	@Input('enablePreview') public enablePreview: boolean = false;
	@Input('item') public item: ListItem | undefined;
	@Input('itemUuidName') public itemUuidName: string | undefined;
	@Input('isLeaf') public isLeaf: boolean = false;
	@Input('isInProgress') public isInProgress: boolean = false;
	@Input('enableClickAsEdit') public enableClickAsEdit: boolean = false;
	@Input('prefix') public prefix: string | undefined;
	@Output('onSelect') onSelectEmitter: EventEmitter<any> = new EventEmitter<any>();
	@Output('onCopy') onCopyEmitter: EventEmitter<any> = new EventEmitter<any>();
	@Output('onEdit') onEditEmitter: EventEmitter<any> = new EventEmitter<any>();
	@Output('onDelete') onDeleteEmitter: EventEmitter<any> = new EventEmitter<any>();
	@Output('onMoveUp') onMoveUpEmitter: EventEmitter<any> = new EventEmitter<any>();
	@Output('onMoveDown') onMoveDownEmitter: EventEmitter<any> = new EventEmitter<any>();

	public canWeDeleteInProgressCourseWork: boolean = false;
	private sub?: Subscription;

	constructor(
		private nav: NavigationService,
		private sectionService: SectionService,
		private clientService: ClientService,
		private message: NzMessageService,
		private authService: AuthService
	) {
		this.sub = this.authService.canWeDeleteInProgressCourseWork$.subscribe(
			(canWeDeleteInProgressCourseWork: boolean) => {
				this.canWeDeleteInProgressCourseWork = canWeDeleteInProgressCourseWork;
			}
		);
	}

	ngOnInit(): void {}

	public async preview(): Promise<void> {
		const params: Record<string, string> = await firstValueFrom(this.nav.matrixParams$);

		const path: string[] = ['/', 'my', 'journey'];

		const newParams: Record<string, string> = {};

		if (params['unitUuid']) {
			newParams['unitUuid'] = params['unitUuid'];
		}

		if (params['lessonUuid']) {
			newParams['lessonUuid'] = params['lessonUuid'];
		}

		if (params['activityUuid']) {
			newParams['activityUuid'] = params['activityUuid'];
		}

		if (!this.item) {
			throw new Error('No item to preview');
		}

		if (this.itemUuidName) {
			newParams[this.itemUuidName] = this.item.uuid;
		}

		const section: Section | undefined =
			await this.sectionService.findFirstSectionMatchingUuids$({
				...newParams,
				clientUuid: params['clientUuid'],
			});

		if (!section) {
			this.message.error('This content is assigned to no sections and cannot be previewed.');
			return;
		}

		if (section) {
			newParams['sectionUuid'] = section.uuid;
			newParams['courseUuid'] = section.course_uuid;
			newParams['locationUuid'] = section.location_uuid;
		}

		const client: Client = await this.clientService.get$(params['clientUuid']);

		this.nav.navigateWithMatrixNewWindow$(client.subdomain, path, newParams, false);
	}

	public onBackgroundClick(event: MouseEvent): void {
		if (event.target !== event.currentTarget) {
			return;
		}

		if (this.enableClickAsEdit) {
			this.edit(event);
		} else {
			this.onSelectEmitter.emit(this.item);
		}
	}

	public select(event: MouseEvent): void {
		event.stopPropagation();
		this.onSelectEmitter.emit(this.item);
	}

	public copy(event: MouseEvent): void {
		event.stopPropagation();
		this.onCopyEmitter.emit(this.item);
	}

	public edit(event: MouseEvent): void {
		event.stopPropagation();
		this.onEditEmitter.emit(this.item);
	}

	public delete(): void {
		this.onDeleteEmitter.emit(this.item);
	}

	public moveUp(event: MouseEvent): void {
		event.stopPropagation();
		this.onMoveUpEmitter.emit(this.item);
	}

	public moveDown(event: MouseEvent): void {
		event.stopPropagation();
		this.onMoveDownEmitter.emit(this.item);
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
